// src/services/donationsService.ts
import axios from "axios";
import { API_BASE_URL } from "../config";
import { LandListItem } from "../types/donations";
import { asErr } from "../utils/apiHelpers";

/**
 * Single map point returned by land map API
 * This matches the JSON returned directly from PostgreSQL via backend.
 */
export type LandMapPoint = {
  id: number;
  sequence?: string;
  latitude: number;
  longitude: number;
  buildingArea?: number;
  landNumber?: string;
  fullAddress?: string;
  donationPriority?: number;
  aproxLandPrice?: number;
  distanceM?: number | null;
};

/**
 * Query parameters for land map API
 * Supports:
 * - region/city/center filtering
 * - price filtering
 * - current location filtering
 * - radius and limit
 */
type LandMapQuery = {
  regionId?: number;
  cityId?: number;
  centerId?: number;
  minPrice?: number;
  maxPrice?: number;
  searchKeyword?: string;
  userLat?: number;
  userLng?: number;
  radiusM?: number;
  limit?: number;
};




type LandListQuery = {
  regionId?: number;
  cityId?: number;
  centerId?: number;
  minPrice?: number;
  maxPrice?: number;
  searchKeyword?: string;
  pageNo: number;
  pageSize: number;
};

export type LandListResponse = {
  items: LandListItem[];
  totalCount: number;
  pageCount: number;
};

// ---- helpers ---------------------------------------------------------------

// drop null/undefined/NaN params so we never send "?regionId=&cityId=null"
function cleanedParams(q: LandListQuery) {
  const out: Record<string, string | number> = {};
  const put = (k: keyof LandListQuery) => {
    const v = q[k];
    if (v === null || v === undefined) return;
    if (typeof v === "number" && !Number.isFinite(v)) return;
    out[k] = v as any;
  };
  put("regionId");
  put("cityId");
  put("centerId");
  put("minPrice");
  put("maxPrice");
  put("searchKeyword");
  // pageNo/pageSize are required; default if insane
  out.pageNo = Number.isFinite(q.pageNo) && q.pageNo > 0 ? q.pageNo : 1;
  out.pageSize = Number.isFinite(q.pageSize) && q.pageSize > 0 ? q.pageSize : 12;
  return out;
}




// Clean map API params so we never send invalid values
// Example bad values we want to avoid:
// ?regionId=&cityId=null&userLat=NaN&limit=0
function cleanedMapParams(q: LandMapQuery) {
  const out: Record<string, string | number> = {};

  const put = (key: keyof LandMapQuery) => {
    const value = q[key];

    // skip null/undefined
    if (value === null || value === undefined) return;

    // skip invalid numbers
    if (typeof value === "number" && !Number.isFinite(value)) return;

    out[key] = value;
  };

  put("regionId");
  put("cityId");
  put("centerId");
  put("minPrice");
  put("maxPrice");
  put("searchKeyword");
  put("userLat");
  put("userLng");

  // radius: keep sane default if caller passes bad value
  out.radiusM =
    typeof q.radiusM === "number" && Number.isFinite(q.radiusM) && q.radiusM > 0
      ? q.radiusM
      : 5000;

  // limit: keep sane default if caller passes bad value
  out.limit =
    typeof q.limit === "number" && Number.isFinite(q.limit) && q.limit > 0
      ? q.limit
      : 2000;

  return out;
}



// Be explicit: if the server returns text/HTML we’ll tell you immediately.
function parseJsonOrThrow(text: string) {
  const t = (text ?? "").toString().trim();
  if (!t) throw new Error("API returned empty response");
  if (t[0] === "<") {
    throw new Error(`API returned HTML instead of JSON (first 160): ${t.slice(0,160)}`);
  }
  try {
    return JSON.parse(t);
  } catch (e) {
    throw new Error(`API returned invalid JSON (first 160): ${t.slice(0,160)}`);
  }
}

// Use relative URLs in development (goes through proxy) or absolute in production
const getBaseURL = () => {
  if (process.env.NODE_ENV === 'development') {
    return ''; // Use relative URLs to go through setupProxy.js
  }
  return API_BASE_URL;
};

// ---- axios instance ---------------------------------------------------------
const http = axios.create({
  baseURL: getBaseURL(),
  headers: { Accept: "application/json" },
  withCredentials: false,
  // Treat 3xx/4xx/5xx as errors so we hit our catch and show body snippet.
  validateStatus: (status) => status >= 200 && status < 300,
  timeout: 60000, // 60 seconds timeout
});

// ---- API --------------------------------------------------------------------
export async function listDonationLands(q: LandListQuery): Promise<LandListResponse> {
  try {
    // Ask backend as TEXT. We'll validate JSON ourselves.
    const { data: text } = await http.get<string>("/api/donations/lands", {
      params: cleanedParams(q),
      responseType: "text",
      // prevent axios from attempting to auto-parse JSON
      transformResponse: [(d) => d as string],
    });

    const data = parseJsonOrThrow(text);

    // Shape check
    if (!data || !Array.isArray(data.items) ||
        typeof data.totalCount !== "number" ||
        typeof data.pageCount !== "number") {
      throw new Error("Unexpected payload shape from API");
    }

    return data as LandListResponse;
  } catch (err) {
    throw asErr("listDonationLands failed", err);
  }
}


/** GET /api/donations/lands/:id -> land detail (PUBLIC) */
export async function getDonationLandById(landId: number) {
  try {
    const { data: text } = await http.get<string>(
      `/api/donations/lands/${encodeURIComponent(landId)}`,
      {
        responseType: "text",
        transformResponse: [(d) => d as string], // keep as text; we validate JSON ourselves
      }
    );

    const data = parseJsonOrThrow(text);

    // Minimal shape check
    if (!data || typeof data !== "object" || typeof (data as any).id !== "number") {
      throw new Error("Unexpected payload shape from API");
    }

    return data; // matches the detail payload you showed
  } catch (err) {
    throw asErr("getDonationLandById failed", err);
  }
}




/**
 * GET /api/donations/lands/map-points
 * Returns land points for map rendering
 *
 * Supports 2 modes:
 * 1) Region/city search
 * 2) Current location search (userLat/userLng + radiusM)
 *
 * Backend already returns JSON array directly, so we only validate it here.
 */
export async function getDonationLandMapPoints(
  q: LandMapQuery
): Promise<LandMapPoint[]> {
  try {
    // Ask backend as TEXT first, same pattern used in your existing service methods.
    // This helps catch HTML/error pages clearly instead of silent JSON parse confusion.
    const { data: text } = await http.get<string>("/api/donations/lands/map-points", {
      params: cleanedMapParams(q),
      responseType: "text",
      transformResponse: [(d) => d as string],
    });

    const data = parseJsonOrThrow(text);

    // Minimal shape check:
    // API must return an array. If not, something is wrong.
    if (!Array.isArray(data)) {
      throw new Error("Unexpected payload shape from land map API");
    }

    return data as LandMapPoint[];
  } catch (err) {
    throw asErr("getDonationLandMapPoints failed", err);
  }
}